/**
 * The app root's error boundary (plan D48, T69): a render error anywhere
 * below it is reported with `source: "boundary"` and replaced by a recover
 * screen, instead of a blank WebView.
 *
 * A class, because React still has no hook for `componentDidCatch`. The React
 * Compiler leaves classes alone, which is fine: it re-renders only when the
 * app has already fallen over.
 *
 * The recover screen is A2's language — canvas, `a-display` heading, the
 * actions in the bottom third — and offers two ways out:
 * - "Recarregar" reloads the page as it is, screen state included;
 * - "Voltar ao início" replaces the URL with the bare path (no search, so the
 *   Today screen with no layer open) and loads it. `replace`, so Back does not
 *   lead into the screen that crashed.
 */
import {
  "errors-boundary.body" as boundaryBody,
  "errors-boundary.home" as boundaryHome,
  "errors-boundary.reload" as boundaryReload,
  "errors-boundary.title" as boundaryTitle,
} from "@repo/i18n";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { appI18n } from "../../features/auth/i18n";
import { Button } from "../../ui/controls/button";
import { reportError } from "./install";

/** The thrown value with React's component stack after its own stack. */
function withComponentStack(error: unknown, info: ErrorInfo): unknown {
  const componentStack = info.componentStack ?? "";
  if (componentStack === "" || !(error instanceof Error)) {
    return error;
  }
  return {
    message: error.message,
    name: error.name,
    stack: `${error.stack ?? ""}\n--- component stack ---${componentStack}`,
  };
}

/**
 * Reports one render error caught by a boundary — this one, or the router's
 * per-route boundary (`defaultOnCatch` in `app/router.tsx`), which catches
 * every error thrown under a route before it could reach the root.
 */
export function reportBoundaryError(error: unknown, info: ErrorInfo): void {
  reportError("boundary", withComponentStack(error, info));
}

function reload(): void {
  window.location.reload();
}

function goHome(): void {
  window.location.replace(window.location.pathname);
}

export function RecoverScreen() {
  // Subscribes to the stored language, so the screen is in the person's
  // language once storage answers even if the app never got that far.
  appI18n.useLocale();
  return (
    <section
      aria-labelledby="error-boundary-title"
      className="mx-auto flex min-h-full w-full max-w-105 flex-col bg-canvas px-4 pt-12 pb-6 lg:justify-center"
      role="alert"
    >
      <h1
        className="a-display text-balance text-3xl text-ink"
        id="error-boundary-title"
      >
        {boundaryTitle()}
      </h1>
      <p className="mt-3 text-base text-ink-2">{boundaryBody()}</p>
      <div className="mt-auto flex flex-col gap-2 pt-8 lg:mt-8">
        <Button onClick={reload} size="lg" variant="primary">
          {boundaryReload()}
        </Button>
        <Button onClick={goHome} size="md" variant="ghost">
          {boundaryHome()}
        </Button>
      </div>
    </section>
  );
}

interface ErrorBoundaryState {
  failed: boolean;
}

export class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  state: ErrorBoundaryState = { failed: false };

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportBoundaryError(error, info);
  }

  render(): ReactNode {
    return this.state.failed ? <RecoverScreen /> : this.props.children;
  }
}
